import React, { useEffect, useMemo, useRef, useState } from 'react';
import ListContent from '../composites/ListContent';
import EmptyState from '../composites/EmptyState';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import userWorkoutsRowStyles from './UserWorkoutsRow.module.scss';
import styles from './ClientsContent.module.scss';
import UserWorkoutsRow from './UserWorkoutsRow';
import VelocityPie from './VelocityPie';
import { useUserAction } from '../../providers';
import { calculateOverallStatistics } from '../../utils';
import { locales } from '../../constants/locale';

import type {
  Member,
  Group,
  SessionStatistic,
  UserStatistics,
} from '../../types';
import type { TrainerDashboardData } from '../../appState/appInitialState';
import { UserType } from '../../constants';

interface ClientsWorkoutsListProps {
  members: Member[];
  groups: Group[];
  trainers: TrainerDashboardData[];
  userType: UserType;
  onArchiveMember: (member: Member) => Promise<void>;
  onOpenEditClient: (member: Member) => void;
  onEditProgram: (member: Member) => void;
  onCreateNewProgram: (member: Member) => void;
  onOpenAddClientDialog: () => void;
  titles: string[];
  sectionTitle?: string;
  showOverallMetrics?: boolean;
  isActive?: boolean;
  onOverallStatisticsChange?: (statistics: UserStatistics | null) => void;
}

const texts = locales.en.components.clients.clientsContent;

const ClientsWorkoutsList: React.FC<ClientsWorkoutsListProps> = ({
  members,
  groups,
  trainers,
  userType,
  onArchiveMember,
  onOpenEditClient,
  onEditProgram,
  onCreateNewProgram,
  onOpenAddClientDialog,
  titles,
  sectionTitle,
  showOverallMetrics = true,
  isActive = true,
  onOverallStatisticsChange,
}) => {
  const { getSessionStatistics } = useUserAction();
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [memberSessionStatistics, setMemberSessionStatistics] = useState<
    Record<string, SessionStatistic[]>
  >({});
  const lastFetchedStatsKeyRef = useRef<string | null>(null);
  const inFlightStatsKeyRef = useRef<string | null>(null);

  const memberIds = useMemo(
    () =>
      members
        .map(({ id }) => id)
        .filter(Boolean)
        .sort(),
    [members],
  );
  const memberIdsKey = memberIds.join('|');

  useEffect(() => {
    if (!isActive) {
      return;
    }
    if (!memberIds.length) {
      setMemberSessionStatistics({});
      lastFetchedStatsKeyRef.current = null;
      inFlightStatsKeyRef.current = null;
      onOverallStatisticsChange?.(null);
      return;
    }
    if (
      memberIdsKey === lastFetchedStatsKeyRef.current ||
      memberIdsKey === inFlightStatsKeyRef.current
    ) {
      return;
    }
    const currentFetchKey = memberIdsKey;
    inFlightStatsKeyRef.current = currentFetchKey;
    const fetchStatistics = async () => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(now.getDate() + 1);
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(now.getDate() - 30);
      try {
        const stats = await getSessionStatistics(
          memberIds,
          thirtyDaysAgo.toISOString(),
          tomorrow.toISOString(),
          true,
        );
        setMemberSessionStatistics(stats);
        lastFetchedStatsKeyRef.current = memberIdsKey;
      } catch {
        setMemberSessionStatistics({});
      } finally {
        if (inFlightStatsKeyRef.current === currentFetchKey) {
          inFlightStatsKeyRef.current = null;
        }
      }
    };
    fetchStatistics();
  }, []);

  useEffect(() => {
    if (!selectedClientId) {
      return;
    }
    if (!members.some((member) => member.id === selectedClientId)) {
      setSelectedClientId('');
    }
  }, [members, selectedClientId]);

  const trainerLookup = useMemo(() => {
    const lookup: Record<string, TrainerDashboardData> = {};
    trainers.forEach((trainer) => {
      lookup[trainer.trainer.id] = trainer;
    });
    return lookup;
  }, [trainers]);

  const statsSummary = useMemo(() => {
    const lastWorkoutMap: Record<string, SessionStatistic | null> = {};
    const last7DayMap: Record<string, SessionStatistic[]> = {};
    const last30DayMap: Record<string, SessionStatistic[]> = {};
    const now = new Date();
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(now.getDate() - 7);
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(now.getDate() - 30);

    Object.entries(memberSessionStatistics).forEach(
      ([memberId, stats = []]) => {
        const sortedStats = [...stats].sort(
          (a, b) =>
            new Date(b.createdDate).getTime() -
            new Date(a.createdDate).getTime(),
        );
        lastWorkoutMap[memberId] = sortedStats[0] ?? null;
        last7DayMap[memberId] = sortedStats.filter(
          (stat) => new Date(stat.createdDate) >= sevenDaysAgo,
        );
        last30DayMap[memberId] = sortedStats.filter(
          (stat) => new Date(stat.createdDate) >= thirtyDaysAgo,
        );
      },
    );

    const overall = calculateOverallStatistics(
      Object.values(lastWorkoutMap).filter((stat): stat is SessionStatistic =>
        Boolean(stat),
      ),
      Object.values(last7DayMap).flat(),
      Object.values(last30DayMap).flat(),
      [],
    );

    return {
      lastWorkoutMap,
      last7DayMap,
      last30DayMap,
      overall,
    };
  }, [memberSessionStatistics]);

  useEffect(() => {
    onOverallStatisticsChange?.(statsSummary.overall);
  }, [statsSummary.overall, onOverallStatisticsChange]);

  const handleSelectClient = (memberId: string) => {
    setSelectedClientId(memberId);
  };

  const renderVelocityPies = () => {
    const {
      lastWorkoutConcentricGoodRepsPercentage,
      lastWorkoutConcentricFastRepsPercentage,
      lastWorkoutConcentricSlowRepsPercentage,
      lastWeekConcentricGoodRepsPercentage,
      lastWeekConcentricFastRepsPercentage,
      lastWeekConcentricSlowRepsPercentage,
      lastMonthConcentricGoodRepsPercentage,
      lastMonthConcentricFastRepsPercentage,
      lastMonthConcentricSlowRepsPercentage,
    } = statsSummary.overall || {};

    const renderPieOrDash = (
      values: {
        good?: number;
        fast?: number;
        slow?: number;
        missed?: number;
      },
      subtitle: string,
    ) => {
      if (!values.good && !values.fast && !values.slow) {
        return (
          <div className={styles.velocityPieContainer}>
            <span className={styles.emptyStat}>-</span>
          </div>
        );
      }
      return (
        <div className={styles.velocityPieContainer}>
          <VelocityPie
            good={values.good}
            fast={values.fast}
            slow={values.slow}
            missed={values.missed}
            subtitle={subtitle}
          />
        </div>
      );
    };

    return (
      <div className={styles.velocityPiesContainer}>
        {renderPieOrDash(
          {
            good: lastWorkoutConcentricGoodRepsPercentage,
            fast: lastWorkoutConcentricFastRepsPercentage,
            slow: lastWorkoutConcentricSlowRepsPercentage,
            missed:
              statsSummary.overall?.lastWorkoutConcentricMissedRepsPercentage,
          },
          texts.lastWorkout,
        )}
        {renderPieOrDash(
          {
            good: lastWeekConcentricGoodRepsPercentage,
            fast: lastWeekConcentricFastRepsPercentage,
            slow: lastWeekConcentricSlowRepsPercentage,
            missed:
              statsSummary.overall?.lastWeekConcentricMissedRepsPercentage,
          },
          texts.last7Days,
        )}
        {renderPieOrDash(
          {
            good: lastMonthConcentricGoodRepsPercentage,
            fast: lastMonthConcentricFastRepsPercentage,
            slow: lastMonthConcentricSlowRepsPercentage,
            missed:
              statsSummary.overall?.lastMonthConcentricMissedRepsPercentage,
          },
          texts.last30Days,
        )}
      </div>
    );
  };

  return (
    <div className={styles.contentContainer}>
      {showOverallMetrics && (
        <div className={styles.contentContainer}>
          <span className={styles.sectionTitle}>{texts.OVERALL_METRICS}</span>
          {renderVelocityPies()}
        </div>
      )}
      {sectionTitle && (
        <span className={styles.sectionTitle}>{sectionTitle}</span>
      )}
      <ListContent
        items={members}
        titles={titles}
        listHeaderClassName={userWorkoutsRowStyles.workoutsListGrid}
        ItemComponent={({ data }) => (
          <UserWorkoutsRow
            groups={groups}
            userType={userType}
            archiveMember={onArchiveMember}
            openEditClientModal={() => onOpenEditClient(data)}
            selected={selectedClientId === data.id}
            onSelect={() => handleSelectClient(data.id)}
            member={data}
            trainer={
              trainerLookup[
                data.trainerIds.find((id) => Boolean(trainerLookup[id])) ?? ''
              ]?.trainer
            }
            onEditProgram={() => onEditProgram(data)}
            onCreateNewProgram={() => onCreateNewProgram(data)}
            lastWorkoutSessionStatistic={
              statsSummary.lastWorkoutMap[data.id] ?? null
            }
            last7DaysSessionStatistics={statsSummary.last7DayMap[data.id] ?? []}
            last30DaysSessionStatistics={
              statsSummary.last30DayMap[data.id] ?? []
            }
          />
        )}
        EmptyStateComponent={() => (
          <EmptyState
            icon={<AccountCircleRoundedIcon />}
            cta={{
              icon: <AddBoxOutlinedIcon color="primary" />,
              label: `Add ${
                userType === UserType.Client ? `A Client` : `An Athlete`
              }`,
              onClick: () => onOpenAddClientDialog(),
            }}
            text="No Clients Have Been Added"
          />
        )}
      />
    </div>
  );
};

export default ClientsWorkoutsList;
